/** Small helpers of the reader: preferences, the sticky offset, paragraph → segment colour, piece lookup, route parsing. */
import type { ChannelId } from '../../engine/channels/types.ts';
import type { ViewModel } from '../../shared/viewmodel.ts';
import { evidenceFill } from '../color.ts';
import { isChannelId } from '../model.ts';
import type { Geometry } from './geometry.ts';
import { spansOfParagraph, splitPieces } from './pieces.ts';

export function readPref(key: string, dflt: boolean): boolean {
	try {
		const v = localStorage.getItem(key);
		return v === null ? dflt : v === '1';
	} catch {
		return dflt;
	}
}
export function writePref(key: string, on: boolean): void {
	try {
		localStorage.setItem(key, on ? '1' : '0');
	} catch {
		/* storage unavailable: the preference lasts for this page only */
	}
}

/**
 * Bottom edge of everything stuck to the top: the shell's sticky band (header, headline, strip, view nav) and the
 * reader's own toolbar, in viewport px.
 */
export function stickyBottom(): number {
	const band = document.querySelector('.stickytop') ?? document.querySelector('.sticky');
	const bar = document.querySelector('.rd-toolbar');
	return (
		(band?.getBoundingClientRect().bottom ?? 0) +
		(bar instanceof HTMLElement ? bar.offsetHeight : 0)
	);
}

const segMemo = new WeakMap<ViewModel, { segOf: Uint16Array; fills: string[] }>();
export function segmentsOf(vm: ViewModel): { segOf: Uint16Array; fills: string[] } {
	const hit = segMemo.get(vm);
	if (hit) return hit;
	const joint = vm.ensemble.segments.joint;
	const segOf = new Uint16Array(vm.paras.count);
	joint.forEach((s, i) => {
		for (let p = s.range.paraStart; p < Math.min(s.range.paraEnd, vm.paras.count); p++)
			segOf[p] = i;
	});
	const r = { segOf, fills: joint.map((s) => evidenceFill(s.index)) };
	segMemo.set(vm, r);
	return r;
}

/** The piece of paragraph `p` that contains character `pos`, under the given layer. */
export function pieceAt(
	vm: ViewModel,
	p: number,
	pos: number,
	slotOf: Int8Array | null
): { a: number; b: number } | null {
	const ids = spansOfParagraph(vm.spans, p);
	const b0 = vm.paras.blockFirst[p] as number;
	const b1 = vm.paras.blockFirst[p + 1] as number;
	const ranges: Array<[number, number]> = [];
	for (let b = b0; b < b1; b++)
		ranges.push([vm.blocks.charStart[b] as number, vm.blocks.charEnd[b] as number]);
	if (ranges.length === 0)
		ranges.push([vm.paras.charStart[p] as number, vm.paras.charEnd[p] as number]);
	for (const [a, e] of ranges) {
		if (pos < a || pos >= e) continue;
		for (const pc of splitPieces(a, e, ids, vm.spans, slotOf))
			if (pc.a <= pos && pos < pc.b) return pc;
	}
	return null;
}

export function lowerBound(arr: Uint32Array, x: number): number {
	let lo = 0;
	let hi = arr.length;
	while (lo < hi) {
		const mid = (lo + hi) >> 1;
		if ((arr[mid] as number) < x) lo = mid + 1;
		else hi = mid;
	}
	return lo;
}

export function parseRange(s: string | null): [number, number] | null {
	if (!s) return null;
	const m = /^(\d+)-(\d+)$/.exec(s);
	if (!m) return null;
	const a = Number(m[1]);
	const b = Number(m[2]);
	return b > a ? [a, b] : null;
}

export function idsOf(s: string | null): ChannelId[] {
	if (!s) return [];
	return s.split(',').filter(isChannelId);
}

/** Changing these three (p, hs, sp) in the route is an arrival; changing only `ch` is not. */
export function tupleOf(p: string | null, h: string | null, sp: string | null): string {
	return `${p ?? ''}|${h ?? ''}|${sp ?? ''}`;
}

/** Set the spacers above and below the rendered window `w` from the geometry (CSSOM height writes). */
export function applySpacers(
	g: Geometry,
	w: { start: number; end: number },
	top: HTMLElement | null,
	bot: HTMLElement | null
): void {
	if (top) top.style.height = `${g.top(w.start)}px`;
	if (bot) bot.style.height = `${Math.max(0, g.total() - g.top(w.end))}px`;
}

/** The reading font size and line height in px, from a rendered paragraph of `region`. */
export function readMetrics(region: HTMLElement | null): { font: number; line: number } | null {
	const el = region?.querySelector('.rd-p');
	if (!el) return null;
	const cs = getComputedStyle(el);
	const font = Number.parseFloat(cs.fontSize);
	let line = Number.parseFloat(cs.lineHeight);
	if (!Number.isFinite(line)) line = font * 1.7;
	return Number.isFinite(font) && font > 0 ? { font, line } : null;
}
